import { Link } from '@inertiajs/react'
import { ProvenanceMarker } from '@/components/ui/ProvenanceMarker'
import { SourceBadgeList } from '@/components/ui/SourceBadge'
import { cn } from '@/utils/cn'
import { STATUS_LABELS } from '@/utils/vocabularyStatus'
import type { CustomVocabularyEntry, VocabularyEntry, WordVocabularyEntry } from '@/types/vocabulary'
import type { WordStatus } from '@/types/word'

/** Short part-of-speech labels for the cramped lemma column. */
const POS_LABELS: Record<string, string> = {
  noun: 'n.', verb: 'v.', adjective: 'adj.', adverb: 'adv.',
  pronoun: 'pron.', preposition: 'prep.', conjunction: 'conj.',
  'infinitive marker': 'part.',
}

interface EntryProps {
  entry: VocabularyEntry
  languageId: number
  onEditCustom: (entry: CustomVocabularyEntry) => void
  onEditStrength: (entry: WordVocabularyEntry) => void
}

/* ─── Lemma ─────────────────────────────────────────────── */

/** Lemma, part of speech, deck and provenance — shared by the row and the card. */
function Lemma({ entry }: { entry: VocabularyEntry }) {
  const rawPos = entry.part_of_speech
  const pos = rawPos ? (POS_LABELS[rawPos.toLowerCase()] ?? rawPos) : ''

  return (
    <>
      <span className="font-serif text-base font-medium text-heading break-words">
        {entry.lemma}
      </span>
      {pos && <span className="ml-1.5 text-xs text-muted">{pos}</span>}
      {entry.type === 'custom' && entry.card_type === 'phrase' && (
        <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded-full text-[10px] font-light border border-border-divider text-muted align-middle whitespace-nowrap">
          {entry.deck_name ?? 'phrase'}
        </span>
      )}
      {/* The marker carries its own visible label, so it replaces the old
          ad-hoc 'custom' text rather than sitting beside it. */}
      {entry.type === 'custom' && <ProvenanceMarker className="ml-1.5" />}
      {entry.type === 'word' && <SourceBadgeList sources={entry.sources} className="ml-1.5" />}
    </>
  )
}

/* ─── Table row ─────────────────────────────────────────── */

export function VocabularyRow({ entry, languageId, onEditCustom, onEditStrength }: EntryProps) {
  const translation = entry.translations.slice(0, 3).join(', ')

  return (
    <tr className="font-light text-body hover:bg-surface-bg/30 transition-colors duration-150">
      <td className="py-3 px-3 pl-4 sm:pl-5 max-w-[220px]">
        <Lemma entry={entry} />
      </td>
      <td className="py-3 px-3 text-sm text-body truncate max-w-[180px]" title={entry.translations.join(', ')}>
        {translation}
      </td>
      <td className="py-3 px-3">
        <StatusBadge status={entry.status} />
      </td>
      <td className="py-3 px-3">
        <StrengthBar easeFactor={entry.ease_factor} status={entry.status} />
      </td>
      <td className="py-3 px-3 text-xs text-muted whitespace-nowrap">
        {formatNextReview(entry.next_review_at)}
      </td>
      <td className="py-3 px-3 pr-4 sm:pr-5">
        <EntryActions
          entry={entry}
          languageId={languageId}
          onEditCustom={onEditCustom}
          onEditStrength={onEditStrength}
        />
      </td>
    </tr>
  )
}

/* ─── Phone card ────────────────────────────────────────── */

/**
 * The same entry as a card, for phone width where the six-column table would otherwise
 * become a horizontal scroller. Translations are shown in full — there is no narrow
 * cell to truncate them into here.
 */
export function VocabularyCard({ entry, languageId, onEditCustom, onEditStrength }: EntryProps) {
  return (
    <li className="rounded-lg border border-border-divider bg-surface-bg/40 px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0">
          <Lemma entry={entry} />
        </p>
        <StatusBadge status={entry.status} />
      </div>

      {entry.translations.length > 0 && (
        <p className="mt-1.5 text-sm font-light text-body">{entry.translations.join(', ')}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <span className="flex items-center gap-3">
          <StrengthBar easeFactor={entry.ease_factor} status={entry.status} />
          <span className="text-xs text-muted whitespace-nowrap">
            {formatNextReview(entry.next_review_at)}
          </span>
        </span>
        <EntryActions
          entry={entry}
          languageId={languageId}
          onEditCustom={onEditCustom}
          onEditStrength={onEditStrength}
        />
      </div>
    </li>
  )
}

/* ─── Actions ───────────────────────────────────────────── */

const actionLink = cn(
  'inline-flex items-center min-h-11 text-xs transition-colors duration-200 rounded whitespace-nowrap',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
)

function EntryActions({ entry, languageId, onEditCustom, onEditStrength }: EntryProps) {
  if (entry.type !== 'word') {
    return (
      <button
        type="button"
        onClick={() => onEditCustom(entry)}
        className={cn(actionLink, 'text-accent hover:text-heading')}
      >
        Edit
      </button>
    )
  }

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onEditStrength(entry)}
        className={cn(actionLink, 'text-muted hover:text-accent')}
      >
        Edit strength
      </button>
      <Link
        href={`/languages/${languageId}/words/${entry.word_id}`}
        className={cn(actionLink, 'text-accent hover:text-heading')}
      >
        Details &rarr;
      </Link>
    </div>
  )
}

/* ─── Status badge ──────────────────────────────────────── */

/**
 * Where a word stands, on its own step of the slate → honey → azure → emerald ramp.
 * "Learning" and "review" used to share one green and "new" was plain grey, so three
 * of the four states looked alike at a glance.
 */
export const STATUS_FILLS: Record<WordStatus, string> = {
  new: 'bg-status-new-bg',
  learning: 'bg-status-learning-bg',
  review: 'bg-status-review-bg',
  mastered: 'bg-status-mastered-bg',
}

export function StatusBadge({ status }: { status: WordStatus }) {
  return (
    <span className={cn(
      'inline-block px-2.5 py-0.5 rounded-full text-xs font-normal text-status-text',
      STATUS_FILLS[status],
    )}>
      {STATUS_LABELS[status]}
    </span>
  )
}

/* ─── Strength bar ──────────────────────────────────────── */

export function StrengthBar({ easeFactor, status }: { easeFactor: number; status: WordStatus }) {
  const level = status === 'new' ? 0 : Math.round(((easeFactor - 1.3) / (3.0 - 1.3)) * 4) + 1
  const clamped = Math.max(0, Math.min(5, level))

  return (
    <div role="img" className="flex items-center gap-0.5" aria-label={`Strength ${clamped} of 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          className={cn(
            'h-1.5 w-3 rounded-full transition-colors duration-200',
            i < clamped ? 'bg-accent' : 'bg-muted/35',
          )}
        />
      ))}
    </div>
  )
}

/* ─── Helpers ───────────────────────────────────────────── */

export function formatNextReview(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays <= 0) return 'Due now'
  if (diffDays === 1) return 'Tomorrow'
  if (diffDays < 7) return `In ${diffDays} days`
  if (diffDays < 30) return `In ${Math.round(diffDays / 7)} weeks`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
